import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  Group,
  Text,
  Stack,
  Button,
  Table,
  Badge,
  ActionIcon,
  Modal,
  TextInput,
  Select,
  NumberFormatter,
  Box,
  Loader,
  Container,
  Title,
  SimpleGrid,
} from '@mantine/core';
import { IconPlus, IconPencil, IconTrash } from '@tabler/icons-react';
import { useSupabase } from '../contexts/SupabaseContext';
import { supabaseAdmin } from '../config/supabase';

const ROLE_OPTIONS = [
  { value: '1', label: 'Super Admin' },
  { value: '2', label: 'Operator' },
  { value: '3', label: 'Read-only' },
];

const roleBadge = (level) => {
  if (level === 1) return <Badge color="red" variant="filled">Super Admin</Badge>;
  if (level === 2) return <Badge color="blue" variant="light">Operator</Badge>;
  return <Badge color="gray" variant="light">Read-only</Badge>;
};

const AdminManagement = () => {
  const { supabase } = useSupabase();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ admin_name: '', email_id: '', address: '', admin_level: '3', password: '' });
  const [, setCurrentUser] = useState(null);
  const [userAdminLevel, setUserAdminLevel] = useState(null);

  // 현재 사용자의 권한 확인
  const checkCurrentUserPermission = useCallback(async () => {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        setError('Authentication required');
        return;
      }

      setCurrentUser(user);

      // admin 테이블에서 현재 사용자의 권한 레벨 조회
      const { data: adminData, error: adminError } = await supabase
        .from('admin')
        .select('admin_level')
        .eq('auth_user_id', user.id)
        .single();

      if (adminError || !adminData) {
        setError('Admin access not found');
        return;
      }

      setUserAdminLevel(adminData.admin_level);

      // Read-only (level 3) 사용자는 페이지 접근 차단
      if (adminData.admin_level === 3) {
        setError('Access denied: Read-only users cannot access this page');
        return;
      }

    } catch (error) {
      setError(`Permission check failed: ${error.message}`);
    }
  }, [supabase]);

  const loadAdmins = useCallback(async () => {
    console.log('loadAdmins called');
    setLoading(true);
    setError('');
    
    const { data, error: err } = await supabase
      .from('admin')
      .select('*')
      .order('admin_level', { ascending: true });
    
    console.log('Raw query result:', { data, error: err });
    
    if (err) {
      console.error('Error loading admins:', err);
      setError(err.message);
      setLoading(false);
      return;
    }
    
    // 디버깅을 위해 auth_user_id 정보 로깅
    console.log('Loaded admins:', data?.map(admin => ({
      id: admin.id,
      email_id: admin.email_id,
      auth_user_id: admin.auth_user_id,
      has_auth_user_id: !!admin.auth_user_id
    })));
    
    console.log('Setting admins state with:', data || []);
    setAdmins(data || []);
    setLoading(false);
  }, [supabase]);

  useEffect(() => {
    const initializePage = async () => {
      await checkCurrentUserPermission();
      if (userAdminLevel !== 3) { // Read-only가 아닌 경우에만 admin 목록 로드
        await loadAdmins();
      }
    };
    initializePage();
  }, [checkCurrentUserPermission, loadAdmins, userAdminLevel]);

  // 모달이 열릴 때마다 폼 초기화 (크롬 자동완성 방지)
  useEffect(() => {
    if (open && !editing) {
      // 새 admin 생성 시에만 완전 초기화
      setForm({ admin_name: '', email_id: '', address: '', admin_level: '3', password: '' });
      setError('');
    }
  }, [open, editing]);

  const totals = useMemo(() => {
    const total = admins.length;
    const superAdmins = admins.filter(a => a.admin_level === 1).length;
    const operators = admins.filter(a => a.admin_level === 2).length;
    const readers = admins.filter(a => a.admin_level === 3).length;
    return { total, superAdmins, operators, readers };
  }, [admins]);

  const openCreate = () => {
    // 상태를 완전히 초기화
    setEditing(null);
    setForm({ admin_name: '', email_id: '', address: '', admin_level: '3', password: '' });
    setError('');
    
    // 약간의 지연을 두고 모달 열기 (상태 업데이트 완료 후)
    setTimeout(() => {
      setOpen(true);
    }, 0);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({
      admin_name: row.admin_name || '',
      email_id: row.email_id || '',
      address: row.address || '',
      admin_level: String(row.admin_level || '3'),
      password: '', // 패스워드는 편집 시 빈 값으로 초기화
    });
    setError('');
    
    // 약간의 지연을 두고 모달 열기 (상태 업데이트 완료 후)
    setTimeout(() => {
      setOpen(true);
    }, 0);
  };

  const saveAdmin = async () => {
    // basic validation to satisfy NOT NULL constraints
    if (!form.email_id || !form.email_id.trim()) {
      setError('Admin ID (Email) is required.');
      return;
    }

    // 새 admin 생성 시 패스워드 검증
    if (!editing && (!form.password || form.password.trim().length < 6)) {
      setError('Password is required and must be at least 6 characters long.');
      return;
    }

    try {
      let authUserId = null;

      if (editing) {
        // 기존 admin 수정
        const payload = {
          admin_name: form.admin_name?.trim() || null,
          email_id: form.email_id?.trim() || null,
          address: form.address?.trim() || null,
          admin_level: parseInt(form.admin_level, 10),
        };
        
        const res = await supabase.from('admin').update(payload).eq('id', editing.id).select();
        if (res.error) {
          setError(res.error.message);
          return;
        }

        // 패스워드가 입력된 경우에만 업데이트
        if (form.password && form.password.trim()) {
          console.log('Attempting to update password for admin:', editing);
          console.log('Password length:', form.password.trim().length);
          
          // auth_user_id가 유효한 UUID인지 확인
          if (!editing.auth_user_id) {
            console.error('No auth_user_id found for admin:', editing);
            setError('Cannot update password: No auth user ID found for this admin.');
            return;
          }
          
          console.log('Auth user ID:', editing.auth_user_id);
          
          // UUID 형식 검증 (간단한 정규식)
          const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
          if (!uuidRegex.test(editing.auth_user_id)) {
            console.error('Invalid UUID format:', editing.auth_user_id);
            setError('Cannot update password: Invalid auth user ID format.');
            return;
          }

          console.log('Updating password via Supabase Auth...');
          const { error: updateError } = await supabaseAdmin.auth.admin.updateUserById(
            editing.auth_user_id,
            { password: form.password.trim() }
          );
          
          if (updateError) {
            console.error('Password update error:', updateError);
            setError(`Failed to update password: ${updateError.message}`);
            return;
          }
          
          console.log('Password updated successfully');
        } else {
          console.log('No password provided, skipping password update');
        }
      } else {
        // 새 admin 생성
        console.log('Creating new admin user...');
        console.log('Email:', form.email_id.trim());
        console.log('Password length:', form.password.trim().length);
        
        // 현재 사용자 권한 확인
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        console.log('Current user creating admin:', currentUser);
        
        // 1. Supabase Auth에 사용자 생성 (Service Role Key 사용)
        console.log('Attempting to create auth user with service role...');
        const { data: authData, error: authError } = await supabaseAdmin.auth.admin.createUser({
          email: form.email_id.trim(),
          password: form.password.trim(),
          email_confirm: true, // 이메일 확인을 자동으로 완료
        });

        console.log('Auth user creation result:', { authData, authError });
        
        if (authError) {
          console.error('Auth user creation failed:', authError);
          setError(`Failed to create auth user: ${authError.message}`);
          return;
        }

        authUserId = authData.user.id;

        // 2. admin 테이블에 레코드 생성
        const payload = {
          admin_name: form.admin_name?.trim() || null,
          email_id: form.email_id?.trim() || null,
          address: form.address?.trim() || null,
          admin_level: parseInt(form.admin_level, 10),
          auth_user_id: authUserId,
        };

        const res = await supabase.from('admin').insert(payload).select();
        if (res.error) {
        // admin 테이블 생성 실패 시 auth 사용자도 삭제
        await supabaseAdmin.auth.admin.deleteUser(authUserId);
          setError(res.error.message);
          return;
        }
      }

      setOpen(false);
      await loadAdmins();
    } catch (error) {
      setError(`Unexpected error: ${error.message}`);
    }
  };

  // RLS 정책 확인 함수
  const checkRLSPolicies = async () => {
    try {
      console.log('Checking RLS policies...');
      
      // 현재 사용자의 권한 확인
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      console.log('Current auth user:', currentUser);
      
      // admin 테이블의 RLS 상태 확인
      const { data: rlsData, error: rlsError } = await supabase
        .rpc('check_admin_permissions', { user_id: currentUser?.id });
      
      console.log('RLS check result:', { rlsData, rlsError });
      
      // 직접 admin 테이블에 대한 권한 테스트
      const { data: testData, error: testError } = await supabase
        .from('admin')
        .select('id')
        .limit(1);
      
      console.log('Direct admin table access test:', { testData, testError });
      
    } catch (error) {
      console.error('RLS check failed:', error);
    }
  };

  const deleteAdmin = async (row) => {
    console.log('Delete admin called with row:', row);
    
    // RLS 정책 확인
    await checkRLSPolicies();
    
    // 삭제 확인
    const confirmMessage = `Are you sure you want to delete admin "${row.admin_name || row.email_id}"? This action cannot be undone.`;
    if (!window.confirm(confirmMessage)) {
      console.log('Delete cancelled by user');
      return;
    }

    try {
      setError(''); // 에러 메시지 초기화
      console.log('Starting delete process for admin ID:', row.id);
      console.log('Current user admin level:', userAdminLevel);
      
      // 삭제 전 현재 레코드 확인
      console.log('Checking record before delete...');
      const { data: beforeData, error: beforeError } = await supabase
        .from('admin')
        .select('*')
        .eq('id', row.id);
      console.log('Record before delete:', { beforeData, beforeError });
      
      // 1. admin 테이블에서 삭제
      console.log('Deleting from admin table...');
      
      // RLS 정책 문제를 해결하기 위해 service role key 사용 시도
      // 또는 현재 사용자의 권한을 다시 확인
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      console.log('Current user for delete:', currentUser);
      
      // 먼저 현재 사용자가 admin_manager인지 확인
      const { data: adminCheck, error: adminCheckError } = await supabase
        .from('admin')
        .select('admin_level')
        .eq('auth_user_id', currentUser.id)
        .single();
      
      console.log('Admin level check:', { adminCheck, adminCheckError });
      
      if (adminCheckError || !adminCheck || adminCheck.admin_level > 2) {
        setError('Insufficient permissions to delete admin records');
        return;
      }
      
      const res = await supabase.from('admin').delete().eq('id', row.id);
      console.log('Delete result:', res);
      console.log('Delete result details:', {
        status: res.status,
        statusText: res.statusText,
        error: res.error,
        data: res.data,
        count: res.count
      });
      
      if (res.error) {
        console.error('Admin table delete error:', res.error);
        setError(`Failed to delete admin: ${res.error.message}`);
        return;
      }

      console.log('Admin deleted successfully from database');

      // 삭제 확인을 위한 추가 쿼리
      console.log('Verifying deletion...');
      const { data: verifyData, error: verifyError } = await supabase
        .from('admin')
        .select('*')
        .eq('id', row.id);
      
      console.log('Verification query result:', { verifyData, verifyError });
      if (verifyData && verifyData.length === 0) {
        console.log('✅ Confirmed: Admin record successfully deleted from database');
      } else {
        console.warn('⚠️ Warning: Admin record still exists in database after delete');
        console.warn('Remaining record:', verifyData);
        
        // RLS 정책 문제일 가능성이 높으므로 에러 메시지 표시
        setError('Failed to delete admin: Record still exists. This may be due to Row Level Security policies.');
        return;
      }

      // 2. auth 사용자도 삭제 (auth_user_id가 있는 경우)
      if (row.auth_user_id) {
        console.log('Deleting auth user:', row.auth_user_id);
        const { error: authError } = await supabaseAdmin.auth.admin.deleteUser(row.auth_user_id);
        if (authError) {
          console.warn('Failed to delete auth user:', authError.message);
          // auth 사용자 삭제 실패는 경고만 표시하고 계속 진행
        } else {
          console.log('Auth user deleted successfully');
        }
      } else {
        console.log('No auth_user_id found, skipping auth user deletion');
      }

      // 3. 로컬 상태에서 즉시 제거 (UI 업데이트)
      setAdmins(prevAdmins => {
        const updatedAdmins = prevAdmins.filter(admin => admin.id !== row.id);
        console.log('Updated local admin list:', updatedAdmins);
        return updatedAdmins;
      });

      // 4. 성공 메시지 표시
      setError(''); // 에러 메시지 초기화
      // 성공 메시지를 잠시 표시 (선택사항)
      console.log(`Admin "${row.admin_name || row.email_id}" deleted successfully`);

      // 5. 서버에서 최신 데이터 다시 로드 (백그라운드에서)
      console.log('Reloading admin list from server...');
      setTimeout(async () => {
        await loadAdmins();
      }, 500);
      
      console.log('Delete process completed successfully');
    } catch (error) {
      console.error('Delete process failed:', error);
      setError(`Failed to delete admin: ${error.message}`);
    }
  };

  // Read-only 사용자는 접근 차단
  if (userAdminLevel === 3) {
    return (
      <Container fluid py="md" pl="xl">
        <Group justify="space-between" mb="sm">
          <Title order={2}>Admin Management</Title>
        </Group>
        <Card withBorder shadow="sm" p="xl" radius="md">
          <Stack align="center" gap="md">
            <Text size="xl" fw={600} c="red">Access Denied</Text>
            <Text size="lg" c="dimmed" ta="center">
              Read-only users cannot access the Admin Management page.
            </Text>
            <Text size="sm" c="dimmed" ta="center">
              Please contact a Super Admin if you need access to this functionality.
            </Text>
          </Stack>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid py="md" pl="xl">
      <Group justify="space-between" mb="sm">
        <Title order={2}>Admin Management</Title>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} mb="xl">
        <Card withBorder shadow="sm" p="md" radius="md">
          <Text size="sm" c="dimmed">Super Admins</Text>
          <Text size="xl" fw={700}><NumberFormatter value={totals.superAdmins} /></Text>
        </Card>
        <Card withBorder shadow="sm" p="md" radius="md">
          <Text size="sm" c="dimmed">Operators</Text>
          <Text size="xl" fw={700}>
            <NumberFormatter value={totals.operators} />
          </Text>
        </Card>
        <Card withBorder shadow="sm" p="md" radius="md">
          <Text size="sm" c="dimmed">Read-only</Text>
          <Text size="xl" fw={700}>
            <NumberFormatter value={totals.readers} />
          </Text>
        </Card>
      </SimpleGrid>

      <Card withBorder>
        <Group justify="space-between" mb="md">
          <Stack gap={0}>
            <Text fw={600}>Administrative Accounts</Text>
            <Text size="sm" c="dimmed">
              {userAdminLevel === 1 
                ? "Manage admin users and their access levels" 
                : "View admin users (read-only access)"
              }
            </Text>
          </Stack>
          {userAdminLevel === 1 && (
            <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Add Admin</Button>
          )}
        </Group>

        {error && (
          <Box mb="md"><Text c="red">{error}</Text></Box>
        )}
        {loading ? (
          <Group justify="center" p="lg"><Loader /></Group>
        ) : (
          <Table striped highlightOnHover withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Admin ID</Table.Th>
                <Table.Th>Name</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Address</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {admins.map((a) => (
                <Table.Tr key={a.id}>
                  <Table.Td>{a.email_id || '-'}</Table.Td>
                  <Table.Td>{a.admin_name || '-'}</Table.Td>
                  <Table.Td>{roleBadge(a.admin_level)}</Table.Td>
                  <Table.Td>{a.address || '-'}</Table.Td>
                  <Table.Td>
                    {userAdminLevel === 1 ? (
                      <Group gap={8}>
                        <ActionIcon variant="subtle" onClick={() => openEdit(a)}>
                          <IconPencil size={16} />
                        </ActionIcon>
                        <ActionIcon color="red" variant="subtle" onClick={() => deleteAdmin(a)}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    ) : (
                      <Text size="sm" c="dimmed">No actions available</Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Modal 
        opened={open} 
        onClose={() => {
          setOpen(false);
          setForm({ admin_name: '', email_id: '', address: '', admin_level: '3', password: '' });
          setError('');
        }} 
        title={editing ? 'Edit Admin' : 'Add Admin'} 
        centered
      >
        <Stack>
          <TextInput
            label="Admin ID (Email)"
            placeholder="name@domain.com"
            value={form.email_id}
            onChange={(e) => setForm((f) => ({ ...f, email_id: e?.currentTarget?.value ?? '' }))}
            required
            autocomplete="new-email"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
          />
          <TextInput
            label="Name"
            placeholder="Admin name"
            value={form.admin_name}
            onChange={(e) => setForm((f) => ({ ...f, admin_name: e?.currentTarget?.value ?? '' }))}
            autocomplete="off"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
          />
          {userAdminLevel === 1 && (
            <TextInput
              label={editing ? "New Password (optional)" : "Initial Password"}
              placeholder={editing ? "Leave empty to keep current password" : "Enter initial password"}
              type="password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e?.currentTarget?.value ?? '' }))}
              required={!editing}
              autocomplete="new-password"
              data-form-type="other"
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              description={
                editing 
                  ? (editing.auth_user_id 
                      ? "Leave empty to keep the current password" 
                      : "This admin has no auth account. Password cannot be updated.")
                    : "Password must be at least 6 characters long"
              }
              disabled={editing && !editing.auth_user_id}
            />
          )}
          {/* Email is managed in auth.users; not stored in public.admin */}
          <TextInput
            label="Address"
            placeholder="0x... or identifier"
            value={form.address}
            onChange={(e) => setForm((f) => ({ ...f, address: e?.currentTarget?.value ?? '' }))}
            autocomplete="off"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore="true"
            data-bwignore="true"
          />
          <Select
            label="Role"
            data={ROLE_OPTIONS}
            value={form.admin_level}
            onChange={(v) => setForm((f) => ({ ...f, admin_level: v ?? '3' }))}
            disabled={userAdminLevel !== 1}
            description={userAdminLevel !== 1 ? "Only Super Admins can change roles" : undefined}
          />
          <Group justify="flex-end">
            <Button 
              variant="default" 
              onClick={() => {
                setOpen(false);
                setForm({ admin_name: '', email_id: '', address: '', admin_level: '3', password: '' });
                setError('');
              }}
            >
              Cancel
            </Button>
            <Button onClick={saveAdmin}>{editing ? 'Save' : 'Add'}</Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default AdminManagement;


